<template>
    <div class="twc" :style="cssProps">
        <div
            ref="viewport"
            :class="['twc__viewport', { 'twc__viewport--dragging': isDragging }]"
            @scroll.passive="onScroll"
            @mousedown="onMouseDown"
        >
            <div class="twc__spacer"></div>
            <div
                v-for="(item, index) in loopedItems"
                :key="index"
                :class="['twc__item', { 'twc__item--active': index === centerIndex }]"
                :style="itemStyle(index)"
                @click="onItemClick(index)"
            >
                {{ item }}
            </div>
            <div class="twc__spacer"></div>
        </div>
    </div>
</template>

<script>
const ITEM_HEIGHT = 36;
const VISIBLE_ROWS = 5;

export default {
    name: 'TimeWheelColumn',

    props: {
        items: { type: Array, default: () => [] },
        value: { type: [String, Number], default: '' },
    },

    data() {
        return {
            centerIndex: 0,
            scrollTop: 0,
            scrollSettleTimer: null,
            isProgrammaticScroll: false,
            _releaseTimer: null,
            _recenterTimer: null,
            _rafId: null,
            // drag
            isDragging: false,
            dragStartY: 0,
            dragStartScroll: 0,
            dragMoved: false,
            suppressClick: false,
        };
    },

    computed: {
        cssProps() {
            return {
                '--twc-item-height': ITEM_HEIGHT + 'px',
                '--twc-height': (ITEM_HEIGHT * VISIBLE_ROWS) + 'px',
            };
        },

        realLen() {
            return this.items.length;
        },

        // Enough copies so the boundary is never reachable by a single gesture.
        // Short lists (e.g. 2 items) get many copies; long lists get fewer.
        copiesCount() {
            if (!this.realLen) return 1;
            const needed = Math.ceil((VISIBLE_ROWS * 12) / this.realLen);
            // keep odd so middleStart is a clean integer
            const odd = needed % 2 === 0 ? needed + 1 : needed;
            return Math.max(9, odd);
        },

        middleStart() {
            return Math.floor(this.copiesCount / 2) * this.realLen;
        },

        loopedItems() {
            if (!this.realLen) return [];
            const total = this.realLen * this.copiesCount;
            const out = new Array(total);
            for (let i = 0; i < total; i++) out[i] = this.items[i % this.realLen];
            return out;
        },
    },

    watch: {
        items() {
            this.$nextTick(() => this.syncFromValue());
        },
        value(newVal) {
            const cur = this.items[this.centerIndex % this.realLen];
            if (String(newVal) === cur) return;
            this.$nextTick(() => this.syncFromValue());
        },
    },

    mounted() {
        this.$nextTick(() => this.syncFromValue());
    },

    beforeDestroy() {
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('mouseup', this.onMouseUp);
        if (this._rafId) cancelAnimationFrame(this._rafId);
        clearTimeout(this.scrollSettleTimer);
        clearTimeout(this._releaseTimer);
        clearTimeout(this._recenterTimer);
    },

    methods: {
        // Continuous scale + opacity — drives the "drum" feel every scroll frame.
        // Color is handled by CSS (.twc__item--active) so it stays clean.
        itemStyle(index) {
            const center  = this.scrollTop / ITEM_HEIGHT;
            const d       = Math.abs(index - center);
            const scaleY  = Math.max(0.72, 1.14 - d * 0.20);
            const opacity = Math.max(0.40, 1.00 - d * 0.22);
            return {
                transform: 'scaleY(' + scaleY.toFixed(3) + ')',
                opacity:   opacity.toFixed(3),
            };
        },

        // ── init ──────────────────────────────────────────────────────────

        syncFromValue() {
            if (!this.realLen) return;
            let realIdx = this.items.indexOf(String(this.value));
            if (realIdx < 0) realIdx = 0;
            const loopIdx = this.middleStart + realIdx;
            this.centerIndex = loopIdx;
            this.jumpTo(loopIdx);
            if (String(this.value) !== this.items[realIdx]) this.emitValue(loopIdx);
        },

        // ── scroll ────────────────────────────────────────────────────────

        onScroll() {
            if (this._rafId) return;
            this._rafId = requestAnimationFrame(() => {
                this._rafId = null;
                const vp = this.$refs.viewport;
                if (!vp) return;

                this.scrollTop = vp.scrollTop;
                this.centerIndex = Math.max(0,
                    Math.min(this.loopedItems.length - 1,
                        Math.round(vp.scrollTop / ITEM_HEIGHT)));

                if (this.isProgrammaticScroll || this.isDragging) return;

                clearTimeout(this.scrollSettleTimer);
                this.scrollSettleTimer = setTimeout(() => {
                    this.snapAndCommit(this.centerIndex);
                }, 120);
            });
        },

        // ── wheel (one step per tick) ─────────────────────────────────────

        // ── click & drag ─────────────────────────────────────────────────

        onItemClick(index) {
            if (this.suppressClick) { this.suppressClick = false; return; }
            this.snapAndCommit(Math.max(0, Math.min(this.loopedItems.length - 1, index)));
        },

        onMouseDown(e) {
            const vp = this.$refs.viewport;
            if (!vp) return;
            this.isDragging   = true;
            this.dragMoved    = false;
            this.dragStartY   = e.clientY;
            this.dragStartScroll = vp.scrollTop;
            window.addEventListener('mousemove', this.onMouseMove);
            window.addEventListener('mouseup',   this.onMouseUp);
            e.preventDefault();
        },

        onMouseMove(e) {
            if (!this.isDragging) return;
            const vp = this.$refs.viewport;
            if (!vp) return;
            const delta = e.clientY - this.dragStartY;
            if (Math.abs(delta) > 3) this.dragMoved = true;
            vp.scrollTop = this.dragStartScroll - delta * 0.6;
        },

        onMouseUp() {
            if (!this.isDragging) return;
            this.isDragging = false;
            window.removeEventListener('mousemove', this.onMouseMove);
            window.removeEventListener('mouseup',   this.onMouseUp);
            this.suppressClick = this.dragMoved;
            this.snapAndCommit(this.centerIndex);
        },

        // ── core helpers ─────────────────────────────────────────────────

        snapAndCommit(loopIdx) {
            this.centerIndex = loopIdx;
            this.smoothScrollTo(loopIdx);
            this.emitValue(loopIdx);
            // After the smooth scroll, silently re-center to middle copy.
            clearTimeout(this._recenterTimer);
            this._recenterTimer = setTimeout(() => this.recenter(), 400);
        },

        recenter() {
            if (!this.realLen) return;
            const realIdx    = this.centerIndex % this.realLen;
            const newLoopIdx = this.middleStart + realIdx;
            if (newLoopIdx === this.centerIndex) return;
            this.centerIndex = newLoopIdx;
            this.jumpTo(newLoopIdx);
        },

        smoothScrollTo(loopIdx) {
            const vp = this.$refs.viewport;
            if (!vp) return;
            const top = loopIdx * ITEM_HEIGHT;
            this.isProgrammaticScroll = true;
            try { vp.scrollTo({ top, behavior: 'smooth' }); }
            catch (e) { vp.scrollTop = top; }
            clearTimeout(this._releaseTimer);
            this._releaseTimer = setTimeout(() => {
                this.isProgrammaticScroll = false;
            }, 400);
        },

        // Instant jump — for init and silent recenter (user sees nothing).
        jumpTo(loopIdx) {
            const vp  = this.$refs.viewport;
            if (!vp) return;
            const top = loopIdx * ITEM_HEIGHT;
            this.isProgrammaticScroll = true;
            try { vp.scrollTo({ top, behavior: 'instant' }); }
            catch (e) { vp.scrollTop = top; }
            this.scrollTop = top;
            clearTimeout(this._releaseTimer);
            this._releaseTimer = setTimeout(() => {
                this.isProgrammaticScroll = false;
            }, 60);
        },

        emitValue(loopIdx) {
            if (!this.realLen) return;
            const value = this.items[loopIdx % this.realLen];
            if (value === undefined || String(this.value) === value) return;
            this.$emit('input', value);
            this.$emit('update:value', value);
        },
    },
};
</script>

<style scoped>
.twc {
    flex: 1;
    height: var(--twc-height, 180px);
    position: relative;
    overflow: hidden;
}

.twc__viewport {
    height: 100%;
    overflow-y: scroll;
    scroll-snap-type: y mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    cursor: grab;
}
.twc__viewport::-webkit-scrollbar { display: none; }

.twc__viewport--dragging {
    scroll-snap-type: none;
    cursor: grabbing;
    user-select: none;
}

.twc__item {
    height: var(--twc-item-height, 36px);
    line-height: var(--twc-item-height, 36px);
    text-align: center;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.01em;
    color: #111111;
    scroll-snap-align: center;
    cursor: pointer;
    user-select: none;
    /* Only colour transitions — transform/opacity update per-frame via :style */
    transition: color 0.18s ease;
    will-change: transform, opacity;
}

.twc__item--active {
    color: var(--twc-accent, #111827);
    font-weight: 600;
}

/* Spacer height = (VISIBLE_ROWS - 1) / 2 * ITEM_HEIGHT
   This lets item[0] snap to the center of the viewport.
   Without it, the first item snaps to the top instead. */
.twc__spacer {
    height: calc((var(--twc-height) - var(--twc-item-height)) / 2);
    flex-shrink: 0;
}
</style>
